/**
 * Dialog Overlay Store
 * 
 * Global state management for dialog overlay visibility.
 * Tracks the number of open dialogs/drawers/sheets and manages overlay state.
 */

import { create } from 'zustand';

interface DialogOverlayState {
  /** Number of currently open dialogs/drawers/sheets */
  openDialogs: number;
  
  /** Whether the overlay should be visible */
  isVisible: boolean;
  
  /** Update overlay state when a dialog opens/closes */
  setOpen: (isOpen: boolean) => void;
  
  /** Reset overlay state (useful on navigation/unmount) */
  reset: () => void;
}

/**
 * Dialog Overlay Store
 * 
 * Usage:
 * ```tsx
 * const { setOpen } = useDialogOverlay();
 * 
 * <Dialog open={open} onOpenChange={(o) => {
 *   setOpen(o);
 *   setDialogOpen(o);
 * }}>
 * ```
 */
export const useDialogOverlay = create<DialogOverlayState>((set, get) => ({
  openDialogs: 0,
  isVisible: false,
  
  setOpen: (isOpen) => {
    const delta = isOpen ? 1 : -1;
    const count = Math.max(0, get().openDialogs + delta);
    const visible = count > 0;
    
    set({ openDialogs: count, isVisible: visible });
  },
  
  reset: () => {
    set({ openDialogs: 0, isVisible: false });
  },
}));
